import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { SeoService } from '@kirigami/platform/browser';
import { TranslatePipe, TranslationService } from '@kirigami/shared/i18n';

/** The invitation that closes an answer, when it has one. */
interface FaqCta {
  readonly label: string;
  readonly suffix: string;
}

/**
 * One FAQ row. The copy lives in the dictionary under
 * `landing.faqs.entries.<slug>`; this only carries what is not copy. An answer
 * is authored as one string with `\n` between paragraphs.
 */
interface FaqEntry {
  readonly slug: string;
  readonly title: string;
  readonly paragraphs: readonly string[];
  readonly bullets: readonly string[];
  readonly cta: FaqCta | null;
  readonly value: number;
}

/**
 * The questions, in the order the design lays them out, each with the shape of
 * its answer: the bullets it ends on, and whether it closes on an invitation to
 * get in touch. The keys are spelled out rather than built from the slug so the
 * compiler checks every one of them.
 */
const FAQS = [
  { slug: 'whatIs' },
  { slug: 'documents' },
  { slug: 'howItHelps' },
  { slug: 'writesForMe' },
  { slug: 'hallucinations' },
  { slug: 'difference' },
  { slug: 'ethics' },
  { slug: 'declare' },
  { slug: 'dataSafety' },
  { slug: 'audience' },
  {
    slug: 'groups',
    cta: {
      label: 'landing.faqs.entries.groups.cta',
      suffix: 'landing.faqs.entries.groups.ctaSuffix',
    },
  },
  {
    slug: 'freePlan',
    bullets: [
      'landing.faqs.entries.freePlan.bullets.upload',
      'landing.faqs.entries.freePlan.bullets.library',
      'landing.faqs.entries.freePlan.bullets.guidelines',
      'landing.faqs.entries.freePlan.bullets.submit',
    ],
  },
] as const;

/**
 * The answer as one string, for the `FAQPage` markup: paragraphs first, then
 * the bullets in reading order, then the closing invitation when there is one.
 * Structured data has to match what a reader sees, so everything the panel
 * renders goes in, separated by single spaces.
 */
function answerText(faq: FaqEntry): string {
  const cta = faq.cta ? [faq.cta.label, faq.cta.suffix] : [];
  return [...faq.paragraphs, ...faq.bullets, ...cta].join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * FAQ accordion, per the landing refinement design: bottom-ruled rows on the
 * paper-blue background, the open question tinted brand blue with its chevron
 * flipped. Keeps the existing single-open button accordion (with ARIA
 * expanded/controls wiring) rather than the design's independent
 * details/summary elements.
 */
@Component({
  selector: 'app-faqs-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <section id="faqs" class="faqs" aria-labelledby="faqs-title">
      <div class="faqs__inner">
        <h2 id="faqs-title" class="faqs__title">{{ 'landing.faqs.title' | t }}</h2>
        <div class="faqs__accordion">
          @for (faq of faqs(); track faq.value) {
            <div class="faqs__panel" [class.faqs__panel--open]="activeIndex() === faq.value">
              <h3 class="faqs__heading">
                <button
                  type="button"
                  class="faqs__header"
                  [attr.aria-expanded]="activeIndex() === faq.value"
                  [attr.aria-controls]="'faq-panel-' + faq.value"
                  [id]="'faq-header-' + faq.value"
                  (click)="toggle(faq.value)"
                >
                  <span class="faqs__question">{{ faq.title }}</span>
                  <svg class="faqs__chevron" viewBox="0 0 19 11" fill="none" aria-hidden="true">
                    <path
                      fill="currentColor"
                      fill-rule="evenodd"
                      d="M.345.345c.46-.46 1.207-.46 1.667 0L9.036 7.37 16.06.345a1.179 1.179 0 1 1 1.666 1.667L9.87 9.869c-.46.46-1.206.46-1.667 0L.345 2.012a1.18 1.18 0 0 1 0-1.667"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </h3>
              <div
                class="faqs__content"
                role="region"
                [id]="'faq-panel-' + faq.value"
                [attr.aria-labelledby]="'faq-header-' + faq.value"
                [hidden]="activeIndex() !== faq.value"
              >
                @for (paragraph of faq.paragraphs; track $index) {
                  <p class="faqs__content-text">{{ paragraph }}</p>
                }
                @if (faq.bullets.length) {
                  <ul class="faqs__list">
                    @for (bullet of faq.bullets; track $index) {
                      <li>{{ bullet }}</li>
                    }
                  </ul>
                }
                @if (faq.cta; as cta) {
                  <p class="faqs__content-text">
                    <button type="button" class="faqs__link" (click)="contact.emit()">
                      {{ cta.label }}
                    </button>
                    {{ cta.suffix }}
                  </p>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .faqs {
        background: var(--kirigami-50);
        padding: 7.375rem 1.5rem 9.375rem;
      }
      @media (min-width: 1024px) {
        .faqs {
          padding: 7.375rem 4.0625rem 9.375rem;
        }
      }
      .faqs__inner {
        max-width: 63.125rem;
        margin: 0 auto;
      }
      .faqs__title {
        margin: 0 0 5.75rem;
        text-align: center;
        font-size: clamp(2rem, 4vw, 2.5rem);
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: -0.011em;
        color: var(--neu-900);
      }
      .faqs__accordion {
        display: flex;
        flex-direction: column;
      }
      .faqs__panel {
        border-bottom: 1px solid var(--kirigami-200);
      }
      /* The question is a heading so the twelve rows appear in the outline and
         screen readers can navigate to them; the reset keeps the row looking
         exactly as it did when the button stood alone. */
      .faqs__heading {
        margin: 0;
        font: inherit;
      }
      .faqs__header {
        width: 100%;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 1.6875rem 0.5rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 2rem;
        text-align: left;
      }
      .faqs__question {
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 1.3;
        color: var(--neu-900);
        transition: color 0.18s ease;
      }
      .faqs__chevron {
        flex: 0 0 auto;
        width: 1.0625rem;
        height: 0.625rem;
        color: var(--neu-600);
        transition:
          rotate 0.22s ease,
          color 0.18s ease;
      }
      .faqs__panel--open .faqs__question,
      .faqs__panel--open .faqs__chevron {
        color: var(--kirigami-500);
      }
      .faqs__panel--open .faqs__chevron {
        rotate: 180deg;
      }
      .faqs__content {
        padding: 0 0.5rem 1.6875rem;
      }
      /* Answers stay in the DOM so crawlers and the prerender pass see them.
         The !important guards the browser default against any later display
         rule on this class, which would otherwise expose all twelve at once. */
      .faqs__content[hidden] {
        display: none !important;
      }
      .faqs__content-text {
        margin: 0 0 0.875rem;
        max-width: 88ch;
        font-size: 1rem;
        line-height: 1.7;
        color: var(--neu-800);
        text-wrap: pretty;
      }
      .faqs__content-text:last-child {
        margin-bottom: 0;
      }
      .faqs__list {
        margin: 0 0 0.875rem;
        padding-left: 1.25rem;
        max-width: 88ch;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        font-size: 1rem;
        line-height: 1.7;
        color: var(--neu-800);
        text-wrap: pretty;
      }
      .faqs__list:last-child {
        margin-bottom: 0;
      }
      .faqs__link {
        padding: 0;
        border: 0;
        background: none;
        font: inherit;
        color: var(--kirigami-600);
        text-decoration: underline;
        cursor: pointer;
      }
      .faqs__link:hover {
        color: var(--kirigami-700);
      }
      @media (prefers-reduced-motion: reduce) {
        .faqs__chevron {
          transition: none;
        }
      }
    `,
  ],
})
export class FaqsWidgetComponent {
  private readonly translations = inject(TranslationService);
  private readonly seo = inject(SeoService);

  constructor() {
    // Rebuilt on locale change; baked into the prerendered landing HTML.
    effect(() => {
      this.seo.setJsonLd('faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: this.faqs().map((faq) => ({
          '@type': 'Question',
          name: faq.title,
          acceptedAnswer: { '@type': 'Answer', text: answerText(faq) },
        })),
      });
    });
    inject(DestroyRef).onDestroy(() => this.seo.removeJsonLd('faq'));
  }

  /**
   * The rows, with their copy resolved for the active locale. A `@for` cannot
   * name a key literally, so the lookup happens here rather than in the
   * template; reading through the service keeps it reactive to a locale change.
   */
  /** Opens the contact modal from the answer that invites a conversation. */
  @Output() readonly contact = new EventEmitter<void>();

  readonly faqs = computed<readonly FaqEntry[]>(() =>
    FAQS.map((faq, index) => ({
      slug: faq.slug,
      title: this.translations.t(`landing.faqs.entries.${faq.slug}.question`),
      paragraphs: this.translations.t(`landing.faqs.entries.${faq.slug}.answer`).split('\n'),
      bullets: ('bullets' in faq ? faq.bullets : []).map((bullet) => this.translations.t(bullet)),
      cta:
        'cta' in faq
          ? {
              label: this.translations.t(faq.cta.label),
              suffix: this.translations.t(faq.cta.suffix),
            }
          : null,
      value: index,
    })),
  );

  readonly activeIndex = signal<number>(-1);

  toggle(value: number): void {
    this.activeIndex.update((current) => (current === value ? -1 : value));
  }
}
